import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";

const GeneratePdf = (props) => {
  const { handlepdfShowHide, pdfShowHide, selectedCol, selectedColData } = props;

  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [filename, setFilename] = useState("Report");
  const [orientation, setOrientation] = useState("landscape");
  const [pageSize, setPageSize] = useState("A4");
  const [theme, setTheme] = useState("striped");
  const [themeColor, setThemeColor] = useState("#2B4C7E");
  const [margin, setMargin] = useState("normal");
  const [showPageNum, setShowPageNum] = useState(true);
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Initialize columns when modal opens
  useEffect(() => {
    if (pdfShowHide && selectedCol && selectedCol[0]) {
      const initialItems = selectedCol[0].map((name, idx) => ({
        id: idx.toString(),
        name: name,
        checked: true,
      }));
      setItems(initialItems);
      // Reset configuration
      setTitle("");
      setFilename("Report");
      setOrientation("landscape");
      setPageSize("A4");
      setTheme("striped");
      setThemeColor("#2B4C7E");
      setMargin("normal");
      setShowPageNum(true);
      setShowTimestamp(true);
      setSearchTerm("");
      setIsGenerating(false);
    } else {
      setItems([]);
    }
  }, [pdfShowHide, selectedCol]);

  // Bulk actions
  const selectAll = () => {
    setItems((prev) => prev.map((item) => ({ ...item, checked: true })));
  };

  const deselectAll = () => {
    setItems((prev) => prev.map((item) => ({ ...item, checked: false })));
  };

  const handleToggle = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  // Reorder click handler
  const moveItem = (index, direction) => {
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    setItems((prev) => {
      const updated = [...prev];
      const current = updated[index];
      updated.splice(index, 1);
      updated.splice(targetIdx, 0, current);
      return updated;
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;
    setItems((prev) => {
      const updated = [...prev];
      const draggedItem = updated[draggedIdx];
      updated.splice(draggedIdx, 1);
      updated.splice(index, 0, draggedItem);
      // Update dragged index to current position
      setDraggedIdx(index);
      return updated;
    });
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
        ]
      : null;
  };

  const exportPDF = () => {
    const unit = "pt";
    // jsPDF options
    const doc = new jsPDF(orientation, unit, pageSize);
    
    // Determine margin values
    let marginVal = 40;
    if (margin === "narrow") marginVal = 20;
    if (margin === "wide") marginVal = 60;

    let startY = 40;

    // Title
    if (title.trim()) {
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(40, 40, 40);
      doc.text(title.trim(), marginVal, 35);
      startY = 55;
    }

    // Timestamp
    if (showTimestamp) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      const timestampText = `Generated on: ${new Date().toLocaleString()}`;
      doc.text(timestampText, marginVal, title.trim() ? 50 : 30);
      startY = title.trim() ? 65 : 42;
    }

    // Filter checked items to build headers & columns index mapping
    const checkedItems = items.filter((item) => item.checked);
    const headers = [checkedItems.map((item) => item.name)];
    
    const data = [];
    selectedColData.forEach((row) => {
      const rowData = [];
      checkedItems.forEach((colItem) => {
        rowData.push(row[colItem.id]);
      });
      data.push(rowData);
    });

    const parsedThemeColor = hexToRgb(themeColor) || [43, 76, 126];

    let content = {
      startY: startY,
      head: headers,
      body: data,
      theme: theme, // 'striped', 'grid', 'plain'
      styles: {
        fontSize: 9,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: parsedThemeColor,
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      margin: marginVal,
      didDrawPage: function (data) {
        if (showPageNum) {
          const str = `Page ${doc.internal.getNumberOfPages()}`;
          doc.setFontSize(9);
          doc.setTextColor(120, 120, 120);
          
          const pSize = doc.internal.pageSize;
          const pHeight = pSize.height ? pSize.height : pSize.getHeight();
          const pWidth = pSize.width ? pSize.width : pSize.getWidth();
          
          doc.text(str, pWidth - marginVal - 30, pHeight - 20);
        }
      },
    };

    doc.autoTable(content);
    doc.save(`${filename || "Report"}.pdf`);
  };

  const handleGenerateClick = () => {
    setIsGenerating(true);
    // Allow the React UI to render the loading state first before blocking the thread
    setTimeout(() => {
      try {
        exportPDF();
      } catch (error) {
        console.error("PDF generation failed:", error);
      } finally {
        setIsGenerating(false);
      }
    }, 400);
  };

  const presets = [
    { name: "Navy", hex: "#2B4C7E" },
    { name: "Teal", hex: "#0D9488" },
    { name: "Forest", hex: "#15803D" },
    { name: "Purple", hex: "#6D28D9" },
    { name: "Ruby", hex: "#BE123C" },
    { name: "Slate", hex: "#334155" },
  ];

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCheckedCount = items.filter((item) => item.checked).length;

  return (
    <Modal
      show={pdfShowHide}
      size="xl"
      dialogClassName="rt-tools-modal"
      centered
      scrollable
      backdrop="static"
      keyboard={false}
    >
      <style>{`
        /* Ensure the modal and backdrop are on top of the sidebar (1050) and header (1060) */
        .modal-backdrop {
          z-index: 1080 !important;
        }
        .modal {
          z-index: 1090 !important;
        }

        /* Glassmorphic backdrop filter and blur */
        .modal-backdrop.show {
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
          background-color: rgba(15, 23, 42, 0.45) !important;
          opacity: 1 !important;
        }

        /* Spring-like transition for scale-in animation */
        .modal.fade .rt-tools-modal.modal-dialog {
          transform: scale(0.95) !important;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }
        .modal.show .rt-tools-modal.modal-dialog {
          transform: scale(1) !important;
        }

        .rt-tools-modal {
          --task-accent: #171717;
          --task-soft: #f5f5f5;
          max-width: 90% !important;
          width: 1200px !important;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
          color: #1e293b !important;
        }

        .rt-tools-modal .modal-content {
          border-radius: 0 !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden !important;
          background: #ffffff !important;
          max-height: calc(100vh - 60px) !important;
          height: auto !important;
          display: flex !important;
          flex-direction: column !important;
          border: 1px solid #cbd5e1 !important;
        }

        .rt-tools-modal .modal-header {
          position: relative !important;
          display: block !important;
          padding: 14px 24px !important;
          background: #ffffff !important;
          border-bottom: 1px solid #edf2f7 !important;
          overflow: hidden !important;
        }

        .rt-tools-modal .modal-header::before {
          display: none !important;
        }

        .rt-tools-modal .rt-header-shell {
          display: flex !important;
          flex-direction: column !important;
          gap: 0 !important;
          width: 100% !important;
        }

        .rt-tools-modal .rt-header-top {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          gap: 16px !important;
          width: 100% !important;
        }

        .rt-tools-modal .rt-header-main {
          display: flex !important;
          align-items: center !important;
          gap: 14px !important;
          min-width: 0 !important;
          flex: 1 1 auto !important;
        }

        .rt-tools-modal .rt-header-copy {
          display: flex !important;
          flex-direction: column !important;
          gap: 0 !important;
          min-width: 0 !important;
          padding-top: 0 !important;
        }

        .rt-tools-modal .rt-modal-title {
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          color: #1e293b !important;
          font-size: 18px !important;
          font-weight: 500 !important;
          line-height: 1.2 !important;
          min-width: 0 !important;
        }

        .rt-tools-modal .rt-header-icon {
          width: 38px !important;
          height: 38px !important;
          min-height: 38px !important;
          border-radius: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #171717 !important;
          background: #f5f5f5 !important;
          border: 1px solid #d4d4d4 !important;
          box-shadow: 0 2px 6px -2px rgba(15,23,42,0.08) !important;
          font-size: 16px !important;
          flex-shrink: 0 !important;
        }

        .rt-tools-modal .rt-modal-title-text {
          display: inline-flex !important;
          align-items: center !important;
          gap: 12px !important;
          min-width: 0 !important;
          flex-wrap: wrap !important;
        }

        .rt-tools-modal .rt-modal-title-badge {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 3px 8px !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.02em !important;
          background: #171717 !important;
          color: #ffffff !important;
          border: 1px solid #171717 !important;
        }

        .rt-tools-modal .modal-body {
          padding: 24px !important;
          background: #f8fafc !important;
          max-height: calc(100vh - 180px) !important;
          overflow-y: auto !important;
          position: relative !important;
        }

        /* Premium Header Close Button */
        .rt-tools-modal .rt-action-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 34px !important;
          height: 34px !important;
          padding: 0 !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          color: #64748b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05) !important;
          outline: none !important;
        }

        .rt-tools-modal .rt-action-btn:hover {
          color: #0f172a !important;
          border-color: #94a3b8 !important;
          background: #f8fafc !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
        }

        .rt-tools-modal .rt-action-btn:active {
          transform: translateY(0) !important;
        }

        .rt-tools-modal .rt-action-btn i {
          font-size: 11px !important;
          color: #94a3b8 !important;
          transition: color 0.2s ease !important;
        }

        .rt-tools-modal .rt-action-btn:hover i {
          color: #ef4444 !important;
        }

        /* Card panels */
        .pdf-card {
          background: white;
          border-radius: 0 !important;
          box-shadow: 0 2px 5px rgba(15,23,42,0.06);
          border: 1px solid #cbd5e1;
          padding: 20px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .pdf-card-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 10px;
        }

        /* Search input styling */
        .pdf-search-bar {
          position: relative;
          margin-bottom: 12px;
        }
        .pdf-search-input {
          width: 100%;
          padding: 8px 12px 8px 32px;
          border-radius: 0 !important;
          border: 1px solid #cbd5e1;
          font-size: 0.85rem;
          transition: all 0.2s;
        }
        .pdf-search-input:focus {
          border-color: var(--task-accent);
          outline: none;
          box-shadow: 0 0 0 3px var(--task-soft);
        }
        .pdf-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 0.85rem;
        }

        /* Column items list */
        .pdf-column-list {
          overflow-y: auto;
          max-height: 380px;
          flex-grow: 1;
          padding-right: 4px;
        }
        .pdf-column-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          border-radius: 0 !important;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          margin-bottom: 8px;
          cursor: grab;
          transition: all 0.2s;
        }
        .pdf-column-item:active {
          cursor: grabbing;
        }
        .pdf-column-item:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }
        .pdf-column-drag-active {
          background: #f8fafc !important;
          border-color: #171717 !important;
          opacity: 0.7;
        }
        .pdf-column-left-side {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-grow: 1;
          min-width: 0;
        }
        .pdf-drag-handle {
          color: #94a3b8;
          cursor: grab;
          font-size: 0.9rem;
          padding: 2px 4px;
        }
        .pdf-checkbox-wrapper {
          display: inline-flex;
          align-items: center;
        }
        .pdf-checkbox {
          width: 16px;
          height: 16px;
          border-radius: 0 !important;
          cursor: pointer;
        }
        .pdf-column-name {
          font-size: 0.85rem;
          font-weight: 500;
          color: #334155;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pdf-order-actions {
          display: flex;
          gap: 4px;
        }
        .pdf-order-btn {
          padding: 2px 6px;
          border-radius: 0 !important;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          font-size: 0.75rem;
          transition: all 0.15s;
          cursor: pointer;
        }
        .pdf-order-btn:hover {
          background: #cbd5e1;
          color: #0f172a;
        }
        .pdf-order-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        /* Config layout */
        .pdf-config-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .pdf-form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .pdf-label {
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #475569 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.02em !important;
          margin-bottom: 4px !important;
        }
        .pdf-input, .pdf-select {
          padding: 8px 12px;
          border-radius: 0 !important;
          border: 1px solid #cbd5e1;
          font-size: 0.85rem;
          background: white;
          transition: all 0.2s;
        }
        .pdf-input:focus, .pdf-select:focus {
          border-color: var(--task-accent);
          outline: none;
          box-shadow: 0 0 0 3px var(--task-soft);
        }

        /* Color bubbles */
        .pdf-color-picker-container {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 4px;
        }
        .pdf-color-bubble {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid transparent;
          transition: all 0.15s ease;
          box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1);
        }
        .pdf-color-bubble:hover {
          transform: scale(1.15);
        }
        .pdf-color-bubble.active {
          border-color: #0f172a;
          transform: scale(1.1);
          box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0f172a;
        }
        .pdf-custom-color-input {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          padding: 0;
          background: none;
        }

        /* Preview area */
        .pdf-preview-container {
          background: #f1f5f9;
          border-radius: 0 !important;
          border: 1px dashed #cbd5e1;
          padding: 16px;
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 180px;
        }
        .pdf-preview-sheet {
          background: white;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1);
          width: 100%;
          max-width: 500px;
          padding: 16px;
          border-radius: 0 !important;
          position: relative;
          transition: all 0.3s ease;
        }
        .pdf-preview-title {
          font-size: 0.95rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2px;
          text-align: left;
        }
        .pdf-preview-meta {
          font-size: 0.65rem;
          color: #64748b;
          margin-bottom: 12px;
          text-align: left;
        }
        .pdf-preview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.6rem;
          margin-bottom: 16px;
        }
        .pdf-preview-table th {
          color: white;
          font-weight: 600;
          text-align: left;
          padding: 4px 6px;
        }
        .pdf-preview-table td {
          padding: 4px 6px;
          color: #475569;
        }
        .pdf-preview-footer {
          display: flex;
          justify-content: space-between;
          font-size: 0.55rem;
          color: #94a3b8;
          margin-top: 10px;
          border-top: 1px solid #f1f5f9;
          padding-top: 6px;
        }

        /* Swtiches */
        .pdf-switch-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 0;
        }
        .pdf-switch-label {
          font-size: 0.8rem;
          color: #475569;
        }
        .pdf-switch-control {
          position: relative;
          display: inline-block;
          width: 36px;
          height: 20px;
        }
        .pdf-switch-control input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .pdf-switch-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: .3s;
          border-radius: 20px;
        }
        .pdf-switch-slider:before {
          position: absolute;
          content: "";
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .3s;
          border-radius: 50%;
        }
        input:checked + .pdf-switch-slider {
          background-color: var(--task-accent);
        }
        input:checked + .pdf-switch-slider:before {
          transform: translateX(16px);
        }

        /* Premium Modal Footer Styles */
        .rt-tools-modal .modal-footer {
          background: #f8fafc !important;
          border-top: 1px solid #edf2f7 !important;
          padding: 16px 24px !important;
          display: flex !important;
          justify-content: flex-end !important;
          gap: 12px !important;
        }
        
        .rt-tools-modal .wf-modal-footer-close-btn {
          display: inline-flex !important;
          align-items: center !important;
          gap: 7px !important;
          padding: 9px 20px !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          color: #475569 !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05) !important;
          outline: none !important;
          cursor: pointer !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .rt-tools-modal .wf-modal-footer-close-btn:hover {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
        }
        
        .rt-tools-modal .wf-modal-footer-close-btn:active {
          transform: translateY(0) !important;
        }
        
        .rt-tools-modal .wf-modal-footer-close-btn i {
          font-size: 11px !important;
          color: #94a3b8 !important;
        }
        
        .rt-tools-modal .wf-modal-footer-close-btn:hover i {
          color: #ef4444 !important;
        }

        .pdf-bulk-actions {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        .pdf-bulk-btn {
          padding: 6px 8px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #171717;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 0 !important;
          cursor: pointer;
          transition: all 0.15s;
          flex-grow: 1;
          text-align: center;
        }
        .pdf-bulk-btn:hover {
          background: #f8fafc;
          color: #000000;
          border-color: #94a3b8;
        }

        /* Custom scrollbar for modal body */
        .rt-tools-modal .modal-body::-webkit-scrollbar {
          width: 6px !important;
          height: 6px !important;
        }
        .rt-tools-modal .modal-body::-webkit-scrollbar-track {
          background: transparent !important;
        }
        .rt-tools-modal .modal-body::-webkit-scrollbar-thumb {
          background: #cbd5e1 !important;
          border-radius: 4px !important;
        }
        .rt-tools-modal .modal-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8 !important;
        }
      `}</style>

      <Modal.Header>
        <div className="rt-header-shell">
          <div className="rt-header-top">
            <div className="rt-header-main">
              <div className="rt-header-icon">
                <i className="fa fa-file-pdf-o" aria-hidden="true"></i>
              </div>
              <div className="rt-header-copy">
                <h4 className="rt-modal-title">
                  <span className="rt-modal-title-text">
                    Interactive PDF Exporter
                    <span className="rt-modal-title-badge">Report Tools</span>
                  </span>
                </h4>
              </div>
            </div>
            <button
              type="button"
              className="rt-action-btn"
              onClick={() => handlepdfShowHide("")}
              title="Close"
              disabled={isGenerating}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Real-time glassmorphic loader overlay */}
        {isGenerating && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
            <div style={{
              padding: "28px 40px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
              border: "1px solid #edf2f7",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px"
            }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "var(--task-accent)" }}></i>
              <div style={{ fontWeight: 750, color: "#0f172a", fontSize: "0.95rem" }}>
                Generating PDF Document
              </div>
              <div style={{ color: "#64748b", fontSize: "0.8rem", textAlign: "center" }}>
                Please wait while we render your custom layout...
              </div>
            </div>
          </div>
        )}

        <div className="row">
          {/* Left Pane - Column Selection */}
          <div className="col-md-5 mb-4 mb-md-0">
            <div className="pdf-card">
              <div className="pdf-card-title">
                <i className="fa fa-list-ul" aria-hidden="true"></i>
                Select & Arrange Columns ({activeCheckedCount} of {items.length} selected)
              </div>

              <div className="pdf-search-bar">
                <i className="fa fa-search pdf-search-icon" aria-hidden="true"></i>
                <input
                  type="text"
                  className="pdf-search-input"
                  placeholder="Search columns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              <div className="pdf-bulk-actions">
                <button type="button" className="pdf-bulk-btn" onClick={selectAll} disabled={isGenerating}>
                  Select All
                </button>
                <button type="button" className="pdf-bulk-btn" onClick={deselectAll} disabled={isGenerating}>
                  Clear All
                </button>
              </div>

              <div className="pdf-column-list">
                {filteredItems.length === 0 ? (
                  <div className="text-center py-4 text-muted" style={{ fontSize: "0.85rem" }}>
                    No matching columns found
                  </div>
                ) : (
                  filteredItems.map((item) => {
                    const actualIdx = items.findIndex((i) => i.id === item.id);
                    const isFirst = actualIdx === 0;
                    const isLast = actualIdx === items.length - 1;
                    const isSearchActive = searchTerm.trim() !== "";

                    return (
                      <div
                        key={item.id}
                        className={`pdf-column-item ${
                          draggedIdx === actualIdx ? "pdf-column-drag-active" : ""
                        }`}
                        draggable={!isSearchActive && !isGenerating}
                        onDragStart={(e) => handleDragStart(e, actualIdx)}
                        onDragOver={(e) => handleDragOver(e, actualIdx)}
                        onDragEnd={handleDragEnd}
                        title={isSearchActive ? "Clear search to enable drag-and-drop" : "Drag to reorder"}
                      >
                        <div className="pdf-column-left-side">
                          {!isSearchActive && (
                            <div className="pdf-drag-handle" title="Drag to reorder">
                              <i className="fa fa-bars" aria-hidden="true"></i>
                            </div>
                          )}
                          <div className="pdf-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="pdf-checkbox mr-2"
                              id={`col_${item.id}`}
                              checked={item.checked}
                              onChange={() => handleToggle(item.id)}
                              disabled={isGenerating}
                            />
                          </div>
                          <label
                            htmlFor={`col_${item.id}`}
                            className="pdf-column-name mb-0 cursor-pointer"
                            style={{ cursor: "pointer" }}
                          >
                            {item.name}
                          </label>
                        </div>

                        <div className="pdf-order-actions">
                          <button
                            type="button"
                            className="pdf-order-btn"
                            disabled={isFirst || isSearchActive || isGenerating}
                            onClick={() => moveItem(actualIdx, -1)}
                            title="Move Up"
                          >
                            <i className="fa fa-arrow-up" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            className="pdf-order-btn"
                            disabled={isLast || isSearchActive || isGenerating}
                            onClick={() => moveItem(actualIdx, 1)}
                            title="Move Down"
                          >
                            <i className="fa fa-arrow-down" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {!searchTerm.trim() && filteredItems.length > 0 && (
                <div className="text-muted mt-2 text-center" style={{ fontSize: "0.75rem" }}>
                  <i className="fa fa-info-circle mr-1"></i> Drag and drop or use arrows to set column order.
                </div>
              )}
            </div>
          </div>

          {/* Right Pane - Config & Live Preview */}
          <div className="col-md-7">
            <div className="pdf-card">
              <div className="pdf-card-title">
                <i className="fa fa-sliders" aria-hidden="true"></i>
                Layout Settings & Styling
              </div>

              <div className="pdf-config-grid">
                <div className="pdf-form-group">
                  <label className="pdf-label">Report Title</label>
                  <input
                    type="text"
                    className="pdf-input"
                    placeholder="e.g. Monthly ACH Report"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isGenerating}
                  />
                </div>

                <div className="pdf-form-group">
                  <label className="pdf-label">Saved Filename</label>
                  <input
                    type="text"
                    className="pdf-input"
                    placeholder="Report"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    disabled={isGenerating}
                  />
                </div>

                <div className="pdf-form-group">
                  <label className="pdf-label">Orientation</label>
                  <select
                    className="pdf-select"
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="landscape">Landscape (Horizontal)</option>
                    <option value="portrait">Portrait (Vertical)</option>
                  </select>
                </div>

                <div className="pdf-form-group">
                  <label className="pdf-label">Page Size</label>
                  <select
                    className="pdf-select"
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="A4">A4</option>
                    <option value="letter">Letter</option>
                    <option value="A3">A3 (Larger)</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>

                <div className="pdf-form-group">
                  <label className="pdf-label">Table Theme</label>
                  <select
                    className="pdf-select"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="striped">Striped Rows</option>
                    <option value="grid">Grid Borders</option>
                    <option value="plain">Minimalist / Plain</option>
                  </select>
                </div>

                <div className="pdf-form-group">
                  <label className="pdf-label">Table Margins</label>
                  <select
                    className="pdf-select"
                    value={margin}
                    onChange={(e) => setMargin(e.target.value)}
                    disabled={isGenerating}
                  >
                    <option value="normal">Normal (40pt)</option>
                    <option value="narrow">Narrow (20pt)</option>
                    <option value="wide">Wide (60pt)</option>
                  </select>
                </div>
              </div>

              <div className="pdf-form-group mt-3">
                <label className="pdf-label">Header Theme Color</label>
                <div className="pdf-color-picker-container">
                  {presets.map((preset) => (
                    <div
                      key={preset.hex}
                      className={`pdf-color-bubble ${
                        themeColor === preset.hex ? "active" : ""
                      }`}
                      style={{ backgroundColor: preset.hex }}
                      onClick={() => !isGenerating && setThemeColor(preset.hex)}
                      title={preset.name}
                    />
                  ))}
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    title="Custom Color"
                  >
                    <input
                      type="color"
                      className="pdf-custom-color-input"
                      value={themeColor}
                      onChange={(e) => setThemeColor(e.target.value)}
                      disabled={isGenerating}
                    />
                    <span style={{ fontSize: "0.75rem", color: "#64748b" }}>Custom</span>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-6">
                  <div className="pdf-switch-container">
                    <span className="pdf-switch-label">Include Page Numbers</span>
                    <label className="pdf-switch-control">
                      <input
                        type="checkbox"
                        checked={showPageNum}
                        onChange={(e) => setShowPageNum(e.target.checked)}
                        disabled={isGenerating}
                      />
                      <span className="pdf-switch-slider"></span>
                    </label>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="pdf-switch-container">
                    <span className="pdf-switch-label">Include Date Timestamp</span>
                    <label className="pdf-switch-control">
                      <input
                        type="checkbox"
                        checked={showTimestamp}
                        onChange={(e) => setShowTimestamp(e.target.checked)}
                        disabled={isGenerating}
                      />
                      <span className="pdf-switch-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Real-time PDF Live Preview Mock */}
              <div className="pdf-preview-container">
                <div className="pdf-label mb-2 text-center" style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                  Live Layout Preview
                </div>
                <div
                  className="pdf-preview-sheet"
                  style={{
                    borderTop: `4px solid ${themeColor}`,
                    transform: orientation === "portrait" ? "scale(0.95)" : "scale(1)",
                    maxWidth: orientation === "portrait" ? "360px" : "100%",
                  }}
                >
                  <div className="pdf-preview-title">{title.trim() || "Untitled Report"}</div>
                  {showTimestamp && (
                    <div className="pdf-preview-meta">
                      Generated on: {new Date().toLocaleDateString()}
                    </div>
                  )}

                  <table
                    className="pdf-preview-table"
                    style={{
                      border: theme === "grid" ? "1px solid #cbd5e1" : "none",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: themeColor }}>
                        {items.filter((i) => i.checked).length === 0 ? (
                          <th style={{ backgroundColor: "#cbd5e1", color: "#475569" }}>No columns selected</th>
                        ) : (
                          items
                            .filter((i) => i.checked)
                            .slice(0, 5)
                            .map((col) => <th key={col.id}>{col.name}</th>)
                        )}
                        {items.filter((i) => i.checked).length > 5 && (
                          <th style={{ backgroundColor: themeColor }}>...</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        style={{
                          backgroundColor: theme === "striped" ? "#f8fafc" : "#ffffff",
                          borderBottom: theme !== "plain" ? "1px solid #e2e8f0" : "none",
                        }}
                      >
                        {items.filter((i) => i.checked).length === 0 ? (
                          <td>-</td>
                        ) : (
                          items
                            .filter((i) => i.checked)
                            .slice(0, 5)
                            .map((col, idx) => <td key={col.id}>Sample Data {idx + 1}</td>)
                        )}
                        {items.filter((i) => i.checked).length > 5 && <td>...</td>}
                      </tr>
                      <tr
                        style={{
                          backgroundColor: "#ffffff",
                          borderBottom: theme !== "plain" ? "1px solid #e2e8f0" : "none",
                        }}
                      >
                        {items.filter((i) => i.checked).length === 0 ? (
                          <td>-</td>
                        ) : (
                          items
                            .filter((i) => i.checked)
                            .slice(0, 5)
                            .map((col, idx) => <td key={col.id}>Sample Data {idx + 1}</td>)
                        )}
                        {items.filter((i) => i.checked).length > 5 && <td>...</td>}
                      </tr>
                    </tbody>
                  </table>

                  <div className="pdf-preview-footer">
                    <span>Report: {filename || "Report"}.pdf</span>
                    {showPageNum && <span>Page 1</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="wf-modal-footer-close-btn"
          onClick={() => handlepdfShowHide("")}
          disabled={isGenerating}
        >
          <i className="fa fa-times" aria-hidden="true"></i> Close
        </button>
        <button
          type="button"
          disabled={activeCheckedCount === 0 || isGenerating}
          onClick={handleGenerateClick}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "9px 20px",
            fontSize: "13px",
            fontWeight: "600",
            color: "white",
            background: isGenerating ? "#64748b" : "#2563eb",
            border: `1px solid ${isGenerating ? "#64748b" : "#2563eb"}`,
            borderRadius: "0",
            cursor: activeCheckedCount === 0 || isGenerating ? "not-allowed" : "pointer",
            boxShadow: "0 2px 5px rgba(37, 99, 235, 0.28), 0 1px 3px rgba(0, 0, 0, 0.12)",
            transition: "all 0.2s"
          }}
        >
          {isGenerating ? (
            <>
              <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> Generating...
            </>
          ) : (
            <>
              <i className="fa fa-cog" aria-hidden="true"></i> Generate PDF
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default GeneratePdf;
